import { AdminTableActions } from "@/components/common/AdminTableActions";
import RecordVisibilityIcon from "@mui/icons-material/Visibility";
import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import {
  Category as CategoryIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Store as StoreIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  AdminDialog,
  AdminEmptyState,
  AdminFilterSummary,
  PageShell,
  AdminTableBodyState,
  AdminTablePanel,
} from "@/components/common";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { getApiConfig } from "@/config/api.config";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import { useTableSorting } from "@/hooks/useTableSorting";
import {
  CategoryService,
  CustomerCategory as CustomerCategoryType,
} from "@/services/features/CategoryService";
import { CraveCategory, CraveCategoryService } from "@/services/features/CraveCategoryService";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import { Store, StoreService } from "@/services/features/StoreService";
import { matchesCategorySearch, splitCategoryNames } from "@/utils/categorySearch";
import {
  isCustomerCategoryRowsPerPage,
  readCustomerCategoryTableSelection,
  writeCustomerCategoryTableSelection,
} from "@/utils/customerCategoryTableSelectionSession";
import { formatInteger } from "@/utils/number";
import { formatStoreSelectorLabel } from "@/utils/store";
import { formatStoreDate, getBrowserTimezone } from "@/utils/storeTime";

import { useCategoryViewFilters } from "./CategoryViewFiltersContext";

interface CustomerOption {
  id: number;
  label: string;
}

type StoreLinkOption = Store | { id: null; label: string };

type SortField = "name" | "gender" | "createdAt" | "lastSeen" | "craveCategory";

const inventoryApiUrl = getApiConfig().inventory;

interface CustomerCategoryProps {
  embedded?: boolean;
}

const CustomerCategory: React.FC<CustomerCategoryProps> = ({ embedded = false }) => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const locale = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const localizedTablePagination = useLocalizedTablePagination();
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [categories, setCategories] = useState<CustomerCategoryType[]>([]);
  const [craveCategories, setCraveCategories] = useState<CraveCategory[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [isStoresLoading, setIsStoresLoading] = useState(false);
  const [defaultStoreId, setDefaultStoreId] = useState<number | null>(null);
  const [storePickerCategoryName, setStorePickerCategoryName] = useState<string | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [craveCategoryInput, setCraveCategoryInput] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [wrapLongCategories, setWrapLongCategories] = useState(false);
  const sharedFilters = useCategoryViewFilters();
  const [localMatchWholeWords, setLocalMatchWholeWords] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const matchWholeWords = sharedFilters?.matchWholeWords ?? localMatchWholeWords;
  const setMatchWholeWords = sharedFilters?.setMatchWholeWords ?? setLocalMatchWholeWords;
  const searchQuery = sharedFilters?.searchQuery ?? localSearchQuery;
  const setSearchQuery = sharedFilters?.setSearchQuery ?? setLocalSearchQuery;

  const customerService = CustomerService.getInstance();
  const categoryService = CategoryService.getInstance();
  const craveCategoryService = CraveCategoryService.getInstance();
  const storeService = useMemo(() => StoreService.getInstance(), []);

  useEffect(() => {
    document.title = t("management.category.customerCategory.documentTitle");
  }, [t]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    let isCurrent = true;

    const fetchStores = async () => {
      setIsStoresLoading(true);
      try {
        const response = await storeService.getStores();
        if (isCurrent) {
          setStores(response.data ?? []);
        }
      } catch (error) {
        console.error("Error fetching stores:", error);
        if (isCurrent) {
          setStores([]);
        }
      } finally {
        if (isCurrent) {
          setIsStoresLoading(false);
        }
      }
    };

    void fetchStores();

    return () => {
      isCurrent = false;
    };
  }, [storeService]);

  useEffect(() => {
    const sharedSelection = readCustomerCategoryTableSelection();
    const customerIdParam = searchParams.get("customerId");
    const customerId =
      customerIdParam === null ? sharedSelection?.customerId : Number(customerIdParam);
    const customer = customers.find((item) => item.id === customerId);
    const pageParam = Number(searchParams.get("page"));
    const rowsPerPageParam = Number(searchParams.get("rowsPerPage"));
    const nextPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
    const nextRowsPerPage = isCustomerCategoryRowsPerPage(rowsPerPageParam)
      ? rowsPerPageParam
      : (sharedSelection?.rowsPerPage ?? 50);

    if ((selectedCustomer?.id ?? null) !== (customer?.id ?? null)) {
      setSelectedCustomer(customer ? { id: customer.id, label: customer.name } : null);
    }

    setPage(nextPage);
    setRowsPerPage(nextRowsPerPage);
    const nextSearchParams = new URLSearchParams(searchParams);
    if (customers.length > 0) {
      if (customer) {
        nextSearchParams.set("customerId", String(customer.id));
      } else {
        nextSearchParams.delete("customerId");
      }
    }
    nextSearchParams.set("page", String(nextPage + 1));
    nextSearchParams.set("rowsPerPage", String(nextRowsPerPage));

    if (nextSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(nextSearchParams, { replace: true });
    }

    if (customers.length > 0) {
      writeCustomerCategoryTableSelection({
        customerId: customer?.id ?? null,
        rowsPerPage: nextRowsPerPage,
      });
    }
  }, [customers, searchParams, selectedCustomer?.id]);

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const response = await customerService.getAllCustomers();
      if (response.success && response.data) {
        setCustomers(response.data);
      } else {
        console.error("Failed to fetch customers:", response.error);
        setCustomers([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async (customerId: number) => {
    setIsCategoriesLoading(true);
    try {
      const categoriesResponse = await categoryService.getByCustomer(customerId);

      if (categoriesResponse.success && categoriesResponse.data) {
        setCategories(categoriesResponse.data);
      } else {
        console.error("Failed to fetch categories:", categoriesResponse.error);
        setCategories([]);
      }

      const craveCategoriesResponse = await craveCategoryService.getByCustomerId(customerId);

      if (craveCategoriesResponse.success && craveCategoriesResponse.data) {
        setCraveCategories(craveCategoriesResponse.data);
      } else {
        console.error("Failed to fetch crave categories:", craveCategoriesResponse.error);
        setCraveCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
      setCraveCategories([]);
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  const handleCustomerChange = (_event: any, value: CustomerOption | null) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    if (value) {
      nextSearchParams.set("customerId", String(value.id));
    } else {
      nextSearchParams.delete("customerId");
    }
    nextSearchParams.set("page", "1");
    nextSearchParams.set("rowsPerPage", String(rowsPerPage));
    setSearchParams(nextSearchParams);
    setSelectedCustomer(value);
    setDefaultStoreId(null);
    writeCustomerCategoryTableSelection({ customerId: value?.id ?? null, rowsPerPage });
    // Clear previous categories when customer changes
    setCategories([]);
    setCraveCategories([]);
    setPage(0);
  };

  const handlePageChange = (nextPage: number) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("page", String(nextPage + 1));
    nextSearchParams.set("rowsPerPage", String(rowsPerPage));
    setSearchParams(nextSearchParams);
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (nextRowsPerPage: number) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("page", "1");
    nextSearchParams.set("rowsPerPage", String(nextRowsPerPage));
    setSearchParams(nextSearchParams);
    setRowsPerPage(nextRowsPerPage);
    writeCustomerCategoryTableSelection({
      customerId: selectedCustomer?.id ?? null,
      rowsPerPage: nextRowsPerPage,
    });
    setPage(0);
  };

  const getCraveCategoryName = (craveCategoryId: number | null | undefined): string => {
    const craveCategory = craveCategories.find((cat) => cat.id === craveCategoryId);
    return craveCategory?.name || "";
  };

  const formatCategoryName = (name: string): string =>
    wrapLongCategories ? splitCategoryNames(name).join("\n") : name;
  const categoryNameSx = {
    display: "block",
    maxWidth: "100%",
    overflowX: "auto",
    scrollbarWidth: "none",
    whiteSpace: wrapLongCategories ? "pre" : "nowrap",
    "&::-webkit-scrollbar": { display: "none" },
  } as const;

  const createNewCraveCategory = async (name: string): Promise<CraveCategory | null> => {
    if (!selectedCustomer) return null;

    try {
      const response = await craveCategoryService.create({
        name,
        customerId: selectedCustomer.id,
      });

      if (response.success && response.data) {
        setCraveCategories((prev) => [...prev, response.data!]);
        return response.data;
      } else {
        console.error("Failed to create crave category:", response.error);
        return null;
      }
    } catch (error) {
      console.error("Error creating crave category:", error);
      return null;
    }
  };

  const saveCraveCategoryAssignment = async (
    customerCategoryId: number,
    craveCategoryId: number | null,
  ) => {
    try {
      const customerCategory = categories.find((cat) => cat.id === customerCategoryId);
      if (!customerCategory) return;

      const updatedCategory = { ...customerCategory, craveCategoryId };
      const response = await categoryService.updateCustomerCategory(updatedCategory);

      if (response.success && response.data) {
        setCategories((prev) =>
          prev.map((cat) => (cat.id === customerCategoryId ? response.data! : cat)),
        );
        setEditingCategoryId(null);
        setCraveCategoryInput("");
      } else {
        console.error("Failed to update customer category:", response.error);
      }
    } catch (error) {
      console.error("Error updating customer category:", error);
    }
  };

  const handleCraveCategoryChange = async (categoryId: number, value: any) => {
    console.log("handleCraveCategoryChange", categoryId, value);
    if (typeof value === "string") {
      const existingCategory = craveCategories.find(
        (cat) => cat.name.toLowerCase() === value.toLowerCase(),
      );
      console.log("existingCategory", existingCategory);
      if (existingCategory) {
        await saveCraveCategoryAssignment(categoryId, existingCategory.id);
      } else if (value.trim()) {
        console.log("createNewCraveCategory", value);
        const newCategory = await createNewCraveCategory(value.trim());
        if (newCategory) {
          console.log("newCategory", newCategory);
          await saveCraveCategoryAssignment(categoryId, newCategory.id);
        }
      }
    } else if (value && typeof value === "object" && "id" in value) {
      await saveCraveCategoryAssignment(categoryId, value.id);
    }
  };

  const handleCraveCategoryEditorClose = () => {
    setEditingCategoryId(null);
    setCraveCategoryInput("");
  };

  const selectedCustomerId = selectedCustomer?.id;
  const customerStores = useMemo(
    () => stores.filter((store) => store.customerId === selectedCustomerId),
    [selectedCustomerId, stores],
  );

  useEffect(() => {
    setDefaultStoreId(null);
  }, [selectedCustomerId]);

  const openCategoryInStore = (storeId: number, categoryName: string) => {
    window.open(
      `${inventoryApiUrl}/sample/${storeId}/category?category=${encodeURIComponent(categoryName)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleCategoryNavigation = (categoryName: string) => {
    const defaultStore = customerStores.find((store) => store.id === defaultStoreId);
    if (defaultStore) {
      openCategoryInStore(defaultStore.id, categoryName);
      return;
    }

    setStorePickerCategoryName(categoryName);
  };

  const noDefaultStoreOption = useMemo<StoreLinkOption>(
    () => ({ id: null, label: t("management.category.customerCategory.options.noDefaultStore") }),
    [t],
  );
  const storeLinkOptions = useMemo<StoreLinkOption[]>(
    () => [noDefaultStoreOption, ...customerStores],
    [customerStores, noDefaultStoreOption],
  );
  const selectedDefaultStore = useMemo<StoreLinkOption>(
    () => customerStores.find((store) => store.id === defaultStoreId) ?? noDefaultStoreOption,
    [customerStores, defaultStoreId, noDefaultStoreOption],
  );
  const getStoreLinkOptionLabel = (option: StoreLinkOption) =>
    "label" in option ? option.label : formatStoreSelectorLabel(option, t, "CRAVE_ADMIN");

  const filteredCategories = categories.filter((category) =>
    [category.name, getCraveCategoryName(category.craveCategoryId)].some((value) =>
      matchesCategorySearch(value, searchQuery, matchWholeWords),
    ),
  );

  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    filteredCategories,
    (category, column) =>
      ({
        name: category.name,
        gender: category.gender,
        createdAt: new Date(category.createdAt).getTime(),
        lastSeen: new Date(category.lastSeen).getTime(),
        craveCategory: getCraveCategoryName(category.craveCategoryId),
      })[column],
    "name" as SortField,
    locale,
  );

  // Fetch categories when selected customer changes
  useEffect(() => {
    if (selectedCustomer) {
      fetchCategories(selectedCustomer.id);
    } else {
      setCategories([]);
      setCraveCategories([]);
    }
  }, [selectedCustomer]);

  const customerOptions: CustomerOption[] = customers.map((customer) => ({
    id: customer.id,
    label: customer.name,
  }));
  const paginatedItems = sortedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const renderPagination = () => (
    <TablePagination
      component="div"
      count={sortedItems.length}
      page={page}
      onPageChange={(_event, nextPage) => handlePageChange(nextPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        handleRowsPerPageChange(Number(event.target.value));
      }}
      rowsPerPageOptions={[25, 50, 100, 250]}
      {...localizedTablePagination}
    />
  );
  const renderTableHeader = () => (
    <Box>
      <TextField
        fullWidth
        label={t("management.category.customerCategory.search.label")}
        placeholder={t("management.category.customerCategory.search.placeholder")}
        value={searchQuery}
        onChange={(event) => {
          setSearchQuery(event.target.value);
          handlePageChange(0);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchQuery ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label={t("common.clear")}
                onClick={() => {
                  setSearchQuery("");
                  handlePageChange(0);
                }}
                edge="end"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "flex-start", lg: "center" },
          gap: 1,
          mt: 1,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Autocomplete
            disableClearable
            getOptionKey={(option) => String(option.id)}
            getOptionLabel={getStoreLinkOptionLabel}
            loading={isStoresLoading}
            onChange={(_event, value) => setDefaultStoreId(value.id)}
            options={storeLinkOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("management.category.customerCategory.fields.defaultStore")}
              />
            )}
            size="small"
            slotProps={{
              paper: {
                sx: {
                  width: { xs: "calc(100vw - 32px)", sm: 540 },
                },
              },
            }}
            sx={{ order: 0, width: { xs: "100%", sm: 360 } }}
            value={selectedDefaultStore}
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{ display: { xs: "none", sm: "block" }, mx: 1.5, order: 1 }}
          />
          <FormControlLabel
            sx={{ color: "text.primary", order: 2 }}
            control={
              <Switch
                checked={wrapLongCategories}
                onChange={(event) => setWrapLongCategories(event.target.checked)}
              />
            }
            label={t("management.category.customerCategory.controls.wrapLongCategories")}
          />
          <FormControlLabel
            sx={{ color: "text.primary", order: 3 }}
            control={
              <Switch
                checked={matchWholeWords}
                onChange={(event) => {
                  setMatchWholeWords(event.target.checked);
                  handlePageChange(0);
                }}
              />
            }
            label={t("management.category.customerCategory.controls.matchWholeWords")}
          />
        </Box>
        <Box sx={{ alignSelf: { xs: "flex-end", lg: "auto" }, marginInlineStart: { lg: "auto" } }}>
          {renderPagination()}
        </Box>
      </Box>
    </Box>
  );

  const content = (
    <>
      <AdminFilterSummary
        filter={
          <MuiAutocompleteMultiSelect
            label={t("management.category.customerCategory.fields.selectCustomer")}
            options={customerOptions}
            value={selectedCustomer ? [selectedCustomer] : []}
            onChange={(next) => handleCustomerChange(null, next[0] ?? null)}
            getOptionLabel={(option) => option.label}
            getOptionKey={(option) => option.id}
            loading={isLoading}
            disabled={isLoading}
          />
        }
        count={categories.length}
        countLabel={t("management.category.customerCategory.text.totalCategories")}
        icon={<CategoryIcon color="primary" sx={{ fontSize: 40 }} />}
      />

      <AdminTablePanel
        header={renderTableHeader()}
        footer={renderPagination()}
        tableSx={{ tableLayout: "fixed" }}

        mobileContent={
          <Box
            sx={{
              minWidth: 0,
              "& .MuiTypography-root": {
                maxWidth: "100%",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              },
            }}
          >
            <RecordSortControl
              value={sortColumn}
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
              options={[
                {
                  value: "name" as const,
                  label: t("management.category.customerCategory.columns.name"),
                },
                {
                  value: "gender" as const,
                  label: t("management.category.customerCategory.columns.gender"),
                },
                {
                  value: "createdAt" as const,
                  label: t("management.category.customerCategory.columns.createdAt"),
                },
                {
                  value: "lastSeen" as const,
                  label: t("management.category.customerCategory.columns.lastSeen"),
                },
                {
                  value: "craveCategory" as const,
                  label: t("management.category.customerCategory.columns.craveCategory"),
                },
              ]}
            />
            <RecordList
              items={paginatedItems}
              getItemId={(category) => category.id}
              loading={isCategoriesLoading}
              resetKey={JSON.stringify([
                sortColumn,
                sortDirection,
                ...paginatedItems.map((category) => category.id),
              ])}
              renderTitle={(category) => {
                return (
                  <>
                    <Typography variant="body2" fontWeight="medium" sx={categoryNameSx}>
                      {formatCategoryName(category.name)}
                    </Typography>
                  </>
                );
              }}
              renderSummary={(category) => {
                return (
                  <RecordFields
                    fields={[
                      {
                        label: <>{t("management.category.customerCategory.columns.gender")}</>,
                        value: (
                          <>
                            {category.gender && (
                              <Chip
                                label={category.gender}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.75rem" }}
                              />
                            )}
                          </>
                        ),
                      },
                      {
                        label: <>{t("management.category.customerCategory.columns.createdAt")}</>,
                        value: (
                          <>
                            <Typography variant="body2">
                              {formatStoreDate(category.createdAt, browserTimezone, locale)}
                            </Typography>
                          </>
                        ),
                      },
                    ]}
                  />
                );
              }}
              renderDetails={(category) => {
                return (
                  <RecordFields
                    fields={[
                      {
                        label: <>{t("management.category.customerCategory.columns.lastSeen")}</>,
                        value: (
                          <>
                            <Typography variant="body2">
                              {formatStoreDate(category.lastSeen, browserTimezone, locale)}
                            </Typography>
                          </>
                        ),
                      },
                      {
                        label: (
                          <>{t("management.category.customerCategory.columns.craveCategory")}</>
                        ),
                        value: (
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (editingCategoryId === category.id) return;

                              setEditingCategoryId(category.id);
                              setCraveCategoryInput(
                                category.craveCategoryId
                                  ? getCraveCategoryName(category.craveCategoryId)
                                  : "",
                              );
                            }}
                            onKeyDown={(event) => {
                              event.stopPropagation();
                              if (
                                editingCategoryId !== category.id &&
                                (event.key === "Enter" || event.key === " ")
                              ) {
                                event.preventDefault();
                                setEditingCategoryId(category.id);
                                setCraveCategoryInput(
                                  category.craveCategoryId
                                    ? getCraveCategoryName(category.craveCategoryId)
                                    : "",
                                );
                              }
                            }}
                            sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                          >
                            {editingCategoryId === category.id ? (
                              <ClickAwayListener onClickAway={handleCraveCategoryEditorClose}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Autocomplete
                                    size="small"
                                    value={craveCategoryInput}
                                    onChange={async (event, value) => {
                                      if (value && typeof value === "object" && "id" in value) {
                                        await handleCraveCategoryChange(category.id, value);
                                      }
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                      setCraveCategoryInput(newInputValue);
                                    }}
                                    options={craveCategories.map((cat) => ({
                                      id: cat.id,
                                      label: cat.name,
                                    }))}
                                    getOptionLabel={(option) =>
                                      typeof option === "string" ? option : option.label
                                    }
                                    freeSolo
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder={t(
                                          "management.category.customerCategory.placeholders.selectOrCreateCraveCategory",
                                        )}
                                        size="small"
                                        onBlur={async () => {
                                          if (craveCategoryInput.trim()) {
                                            await handleCraveCategoryChange(
                                              category.id,
                                              craveCategoryInput.trim(),
                                            );
                                          } else {
                                            handleCraveCategoryEditorClose();
                                          }
                                        }}
                                        onKeyDown={async (e) => {
                                          if (e.key === "Escape") {
                                            handleCraveCategoryEditorClose();
                                          } else if (
                                            e.key === "Enter" &&
                                            craveCategoryInput.trim()
                                          ) {
                                            e.preventDefault();
                                            await handleCraveCategoryChange(
                                              category.id,
                                              craveCategoryInput.trim(),
                                            );
                                          }
                                        }}
                                      />
                                    )}
                                    renderOption={(props, option) => (
                                      <li {...props}>
                                        {typeof option === "string" ? option : option.label}
                                      </li>
                                    )}
                                    sx={{ flex: 1, minWidth: 0 }}
                                  />
                                  <Tooltip title={t("common.clear")}>
                                    <IconButton
                                      size="small"
                                      aria-label={t("common.clear")}
                                      onMouseDown={(event) => event.preventDefault()}
                                      onClick={() =>
                                        void saveCraveCategoryAssignment(category.id, null)
                                      }
                                      sx={{
                                        color: "error.main",
                                        "&:hover": { color: "error.dark", bgcolor: "action.hover" },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </ClickAwayListener>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  className="editable-cell-text"
                                  color="text.primary"
                                  sx={categoryNameSx}
                                >
                                  {category.craveCategoryId
                                    ? formatCategoryName(
                                        getCraveCategoryName(category.craveCategoryId),
                                      )
                                    : "-"}
                                </Typography>
                                <Box
                                  component="span"
                                  className="editable-cell-icon"
                                  sx={{ display: "inline-flex", p: "5px", color: "text.primary" }}
                                >
                                  <EditIcon fontSize="small" />
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ),
                      },
                    ]}
                  />
                );
              }}
              renderActions={(category) => (
                <AdminTableActions
                  actions={[
                    {
                      icon: <RecordVisibilityIcon />,
                      hint: t("common.breadcrumbs.details"),
                      action: () => handleCategoryNavigation(category.name),
                    },
                  ]}
                />
              )}
              emptyState={
                <AdminEmptyState
                  icon={<CategoryIcon sx={{ fontSize: 40 }} />}
                  text={
                    selectedCustomer
                      ? t("management.category.customerCategory.empty.noCategories")
                      : t("management.category.customerCategory.empty.selectCustomer")
                  }
                />
              }
            />
          </Box>
        }
      >
        <TableHead>
          <TableRow>
            <SortableTableCell
              activeColumn={sortColumn}
              column="name"
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
              sx={{ width: "25%", maxWidth: "25%" }}
            >
              {t("management.category.customerCategory.columns.name")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="gender"
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
            >
              {t("management.category.customerCategory.columns.gender")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="createdAt"
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
            >
              {t("management.category.customerCategory.columns.createdAt")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="lastSeen"
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
            >
              {t("management.category.customerCategory.columns.lastSeen")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="craveCategory"
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
              sx={{ width: "25%", minWidth: "25%" }}
            >
              {t("management.category.customerCategory.columns.craveCategory")}
            </SortableTableCell>
          </TableRow>
        </TableHead>
        <AdminTableBodyState
          items={paginatedItems}
          isLoading={isCategoriesLoading}
          colSpan={5}
          loadingLabel={t("common.loading")}
          emptyState={
            <AdminEmptyState
              icon={<CategoryIcon sx={{ fontSize: 40 }} />}
              text={
                selectedCustomer
                  ? t("management.category.customerCategory.empty.noCategories")
                  : t("management.category.customerCategory.empty.selectCustomer")
              }
            />
          }
        >
          {(category) => (
            <TableRow
              key={category.id}
              hover
              role="link"
              tabIndex={0}
              onClick={() => handleCategoryNavigation(category.name)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleCategoryNavigation(category.name);
                }
              }}
              sx={{
                cursor: "pointer",
                "&:hover, &.MuiTableRow-hover:hover": {
                  bgcolor: "action.hover",
                  "& .MuiTableCell-root": { bgcolor: "action.hover" },
                },
              }}
            >
              <TableCell sx={{ width: "25%", maxWidth: "25%" }}>
                <Typography variant="body2" fontWeight="medium" sx={categoryNameSx}>
                  {formatCategoryName(category.name)}
                </Typography>
              </TableCell>
              <TableCell>
                {category.gender && (
                  <Chip
                    label={category.gender}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.75rem" }}
                  />
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatStoreDate(category.createdAt, browserTimezone, locale)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatStoreDate(category.lastSeen, browserTimezone, locale)}
                </Typography>
              </TableCell>
              <TableCell
                role="button"
                tabIndex={0}
                onClick={(event) => {
                  event.stopPropagation();
                  if (editingCategoryId === category.id) return;

                  setEditingCategoryId(category.id);
                  setCraveCategoryInput(
                    category.craveCategoryId ? getCraveCategoryName(category.craveCategoryId) : "",
                  );
                }}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (
                    editingCategoryId !== category.id &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    setEditingCategoryId(category.id);
                    setCraveCategoryInput(
                      category.craveCategoryId
                        ? getCraveCategoryName(category.craveCategoryId)
                        : "",
                    );
                  }
                }}
                sx={{
                  width: "25%",
                  minWidth: "25%",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "action.hover",
                    "& .editable-cell-icon, & .editable-cell-text": { color: "primary.main" },
                  },
                }}
              >
                {editingCategoryId === category.id ? (
                  <ClickAwayListener onClickAway={handleCraveCategoryEditorClose}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Autocomplete
                        size="small"
                        value={craveCategoryInput}
                        onChange={async (event, value) => {
                          if (value && typeof value === "object" && "id" in value) {
                            await handleCraveCategoryChange(category.id, value);
                          }
                        }}
                        onInputChange={(event, newInputValue) => {
                          setCraveCategoryInput(newInputValue);
                        }}
                        options={craveCategories.map((cat) => ({
                          id: cat.id,
                          label: cat.name,
                        }))}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.label
                        }
                        freeSolo
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder={t(
                              "management.category.customerCategory.placeholders.selectOrCreateCraveCategory",
                            )}
                            size="small"
                            onBlur={async () => {
                              if (craveCategoryInput.trim()) {
                                await handleCraveCategoryChange(
                                  category.id,
                                  craveCategoryInput.trim(),
                                );
                              } else {
                                handleCraveCategoryEditorClose();
                              }
                            }}
                            onKeyDown={async (e) => {
                              if (e.key === "Escape") {
                                handleCraveCategoryEditorClose();
                              } else if (e.key === "Enter" && craveCategoryInput.trim()) {
                                e.preventDefault();
                                await handleCraveCategoryChange(
                                  category.id,
                                  craveCategoryInput.trim(),
                                );
                              }
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>{typeof option === "string" ? option : option.label}</li>
                        )}
                        sx={{ flex: 1, minWidth: 0 }}
                      />
                      <Tooltip title={t("common.clear")}>
                        <IconButton
                          size="small"
                          aria-label={t("common.clear")}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => void saveCraveCategoryAssignment(category.id, null)}
                          sx={{
                            color: "error.main",
                            "&:hover": { color: "error.dark", bgcolor: "action.hover" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ClickAwayListener>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      className="editable-cell-text"
                      color="text.primary"
                      sx={categoryNameSx}
                    >
                      {category.craveCategoryId
                        ? formatCategoryName(getCraveCategoryName(category.craveCategoryId))
                        : "-"}
                    </Typography>
                    <Box
                      component="span"
                      className="editable-cell-icon"
                      sx={{ display: "inline-flex", p: "5px", color: "text.primary" }}
                    >
                      <EditIcon fontSize="small" />
                    </Box>
                  </Box>
                )}
              </TableCell>
            </TableRow>
          )}
        </AdminTableBodyState>
      </AdminTablePanel>
      <AdminDialog
        icon={<StoreIcon />}
        maxWidth="sm"
        onClose={() => setStorePickerCategoryName(null)}
        open={storePickerCategoryName !== null}
        title={t("management.category.customerCategory.dialogs.selectStore")}
      >
        <AdminTablePanel
          elevation={0}
          tableProps={{ size: "small" }}
          mobileContent={
            <Box
              sx={{
                minWidth: 0,
                "& .MuiTypography-root": {
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                },
              }}
            >
              <RecordList
                items={customerStores}
                getItemId={(store) => store.id}
                loading={isStoresLoading}
                resetKey={JSON.stringify([null, null, ...customerStores.map((store) => store.id)])}
                renderTitle={(store) => {
                  return <>{getStoreLinkOptionLabel(store)}</>;
                }}
                renderSummary={(store) => {
                  return (
                    <RecordFields
                      fields={[
                        {
                          label: <>{t("commandCenter.fields.storeId")}</>,
                          value: <>{formatInteger(store.id, locale)}</>,
                        },
                      ]}
                    />
                  );
                }}

                renderActions={(store) => {
                  return (
                    <AdminTableActions
                      actions={[
                        {
                          icon: <RecordVisibilityIcon />,
                          hint: t("common.breadcrumbs.details"),
                          action: () => {
                            if (storePickerCategoryName) {
                              openCategoryInStore(store.id, storePickerCategoryName);
                            }
                            setStorePickerCategoryName(null);
                          },
                        },
                      ]}
                    />
                  );
                }}
                emptyState={
                  <AdminEmptyState
                    icon={<StoreIcon sx={{ fontSize: 40 }} />}
                    text={t("management.stores.stores.empty.noStores")}
                  />
                }
              />
            </Box>
          }
        >
          <TableHead>
            <TableRow>
              <TableCell>{t("commandCenter.fields.storeId")}</TableCell>
              <TableCell>{t("management.stores.stores.fields.storeName")}</TableCell>
            </TableRow>
          </TableHead>
          <AdminTableBodyState
            colSpan={2}
            emptyState={
              <AdminEmptyState
                icon={<StoreIcon sx={{ fontSize: 40 }} />}
                text={t("management.stores.stores.empty.noStores")}
              />
            }
            isLoading={isStoresLoading}
            items={customerStores}
            loadingLabel={t("common.loading")}
          >
            {(store) => (
              <TableRow
                hover
                key={store.id}
                onClick={() => {
                  if (storePickerCategoryName) {
                    openCategoryInStore(store.id, storePickerCategoryName);
                  }
                  setStorePickerCategoryName(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    if (storePickerCategoryName) {
                      openCategoryInStore(store.id, storePickerCategoryName);
                    }
                    setStorePickerCategoryName(null);
                  }
                }}
                role="button"
                sx={{
                  cursor: "pointer",
                  "&:hover, &.MuiTableRow-hover:hover": {
                    bgcolor: "action.hover",
                    "& .MuiTableCell-root": { bgcolor: "action.hover" },
                  },
                }}
                tabIndex={0}
              >
                <TableCell>{formatInteger(store.id, locale)}</TableCell>
                <TableCell>{getStoreLinkOptionLabel(store)}</TableCell>
              </TableRow>
            )}
          </AdminTableBodyState>
        </AdminTablePanel>
      </AdminDialog>
    </>
  );

  if (embedded) return content;

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.category.customerCategory.title")}
      icon={<CategoryIcon />}
    >
      {content}
    </PageShell>
  );
};

export default CustomerCategory;
